import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Show the product's first gallery image on a plain product URL.
    // Honor a variant-specific URL, and use selection events for subsequent color changes.
    if (this.dataset.variantImageFromEvent === 'true') {
      delete this.dataset.variantImageFromEvent;
    } else {
      const variantId = new URLSearchParams(window.location.search).get('variant');
      if (variantId) this.#selectVariantImage(variantId);
    }
  }

  #controller = new AbortController();

  #latestVariantRequest = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /** Select the slide associated with a variant's media ID. */
  #selectVariantImage(variantId, requestId = this.#latestVariantRequest) {
    if (!variantId || !this.dataset.variantMediaMap) return;

    const mediaByVariant = new Map(
      this.dataset.variantMediaMap.split(',').map((pair) => pair.split(':'))
    );
    const mediaId = mediaByVariant.get(String(variantId));
    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    if (!slides.length) return;

    const matchingIndex = mediaId
      ? slides.findIndex((slide) =>
          slide.querySelector('.product-media[data-media-id]')?.dataset.mediaId === mediaId
        )
      : -1;
    const index = matchingIndex >= 0 ? matchingIndex : 0;

    const slideshow = this.slideshow || this.querySelector('slideshow-component');
    if (!slideshow) return;

    const selectWhenReady = (attempt = 0) => {
      if (!this.isConnected || requestId !== this.#latestVariantRequest) return;

      slideshow.select(index, undefined, { animate: false });

      // Horizon can take a moment to initialize the slideshow.
      // Retry until the requested slide is selected or about 2 seconds have passed.
      if (slideshow.current !== index && attempt < 120) {
        requestAnimationFrame(() => selectWhenReady(attempt + 1));
      }
    };

    selectWhenReady();
  }

  /**
   * Handles a product select event by selecting the associated media in the existing gallery.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const requestId = ++this.#latestVariantRequest;
    const selectVariant = (variantId) => {
      if (requestId !== this.#latestVariantRequest || !variantId) return;
      this.#selectVariantImage(variantId, requestId);
    };

    const variantId = event.detail?.variantId;
    if (variantId) {
      selectVariant(variantId);
      return;
    }

    event.promise
      .then(({ detail }) => {
        selectVariant(detail?.resource?.id);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[media-gallery] Event promise rejected:', error);
        }
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
